import React, { FC } from 'react';
import Accordion from '~ComponentsImproved/Accordion';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useDispatch, useI18n } from '~Hooks';
import { Box, Button } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Icon from '~ComponentsImproved/Icon';
import SyncIcon from '~Assets/icons/sync.svg';
import { DateTime } from 'luxon';
import { ErrorCase } from '~reducers';
import * as actions from '~actions';

interface Props {
  error: Omit<
    ErrorCase & {
      id: string;
      createdAt: Date;
    },
    'type'
  >;
}

/** A specific expand:able System error */
const ErrorRow: FC<Props> = ({ error }) => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();

  const getMainTitle = () => {
    switch (error.errorType) {
      case 'ORDER_SYNC':
        return i18n('Ordersyncer.Title');
      case 'SKV':
        return i18n('SKV.UnableContact.Srv4pos.Title');
      default:
        return 'General.Error';
    }
  };

  /** We sync automatically and therefore customer should not be able to dismiss it since we'll do it when it has re-synced */
  const showDismissButton = error.errorType !== 'ORDER_SYNC';

  const dismissButton = useButton({
    onClick: () =>
      dispatch({
        type: actions.CLEAR_ERROR,
        id: error.id,
      }),
  });

  return (
    <Accordion
      title={
        <Box display="flex" alignItems="center" gap="xs">
          <Icon src={SyncIcon} height="32px" width="32px" color="var(--colorUIRed)" />
          <Box display="flex" flexDirection="column">
            <Paragraph level={5}>
              {DateTime.fromJSDate(error.createdAt).toFormat('HH:mm')}
            </Paragraph>
            <Heading level={4}>{getMainTitle()}</Heading>
          </Box>
        </Box>
      }
      dark={false}>
      <Box display="flex" flexDirection="column" gap="xs">
        <Heading level={4}>{error.title}</Heading>

        <Paragraph level={4}>{error.message}</Paragraph>

        <Paragraph level={5} color="var(--colorBlueGray1200)">
          {i18n('Ordersyncer.AffectedOrderId')}: {error.orderId}
        </Paragraph>
      </Box>

      {showDismissButton && (
        <Box display="flex" justifyContent="flex-end" marginTop="small">
          <Button {...dismissButton}>{i18n('PrinterStatus.Errors.Dismiss')}</Button>
        </Box>
      )}
    </Accordion>
  );
};

export default React.memo(ErrorRow);
